/**
 * OfficerTaskView (OTV) — mirrors FOM mobile OfficerTaskView
 *
 * Full-featured officer shift dashboard:
 *   - Badge image (gold/silver) with call-sign overlay
 *   - Post assignments with real post icons + TimeBadge (Now / Done / Upcoming)
 *   - Break schedule
 *   - Equipment cards with real icons (radio, key, recorder)
 *   - Secondary tasks with post icons
 *   - Notes / Special details
 *   - Exec roles can browse any officer; regular officers see own data
 */
import { useState, useEffect } from 'react'
import { useAuthStore, isExecRole } from '../../stores/auth'
import { useMobileStore } from '../../stores/mobile'
import { useSyncStore, syncColor } from '../../stores/sync'
import { SUPERVISOR_BADGE, OFFICER_BADGE, FCPD_BADGE, KEY_ICON, WALKIE_ICON_BG, REC_ICON, getPostIcon } from '../../assets'
import FlashSVG from '../equipment/FlashSVG'
import { normalizeCS, getTasksForCS, csNumber } from '../personnel/personnelHelpers'
import ProfileModal from '../layout/modals/ProfileModal'

/* ─── helpers ─── */
function lastName(n: string) {
  if (!n) return ''
  const s = n.replace(/\s*\([^)]*\)\s*/g, '').trim() || n.trim()
  if (s.includes(',')) return s.split(',')[0].trim()
  const parts = s.split(/\s+/)
  return parts[parts.length - 1]
}
function rolePrefix(rank: string) {
  if (!rank) return ''
  const r = rank.toLowerCase()
  if (r === 'developer') return 'Dev.'
  if (r.includes('director of') || r === 'director') return 'Dir.'
  if (r.includes('assistant')) return 'AD.'
  if (r.includes('lieutenant') || r.startsWith('lt')) return 'Lt.'
  if (r.includes('sergeant') || r.includes('sgt') || r === 'supervisor') return 'Sgt.'
  if (r.includes('dispatch')) return 'Disp.'
  if (r.includes('officer') || r.includes('ofc')) return 'Ofc.'
  if (r === 'oic') return 'OIC'
  return ''
}
function displayName(name: string, role: string, rank?: string) {
  const pfx = rolePrefix(rank || role)
  const ln = lastName(name)
  return pfx ? `${pfx} ${ln}` : ln
}
function roleLabel(role: string) {
  if (!role) return ''
  const r = role.toLowerCase()
  if (['developer', 'oic'].includes(r) || r.includes('dir') || r.includes('lieu') || r.includes('lt') || r.includes('assistant') || r.includes('serg') || r.includes('sgt') || r === 'supervisor') return 'Supervisor'
  if (r.includes('dispatch')) return 'Dispatcher'
  if (r.includes('officer') || r.includes('ofc')) return 'Officer'
  return role
}
function isGold(role: string) {
  if (!role) return false
  const r = role.toLowerCase()
  return r.includes('dir') || r.includes('ad') || r.includes('lt') || r.includes('lieu') || r.includes('serg') || r.includes('sgt') || r === 'supervisor' || r.includes('developer') || r === 'oic'
}
function getBadgeImg(role: string) {
  if (role === 'FCPD') return FCPD_BADGE
  return isGold(role) ? SUPERVISOR_BADGE : OFFICER_BADGE
}
function fmtTime(t: string) {
  if (!t) return ''
  const s = String(t)
  if (s.includes(':')) { const p = s.split(':'); return p[0].padStart(2, '0') + ':' + p[1].padStart(2, '0') }
  let padded = s
  if (padded.length === 3) padded = '0' + padded
  if (padded.length !== 4) return t
  return padded.substring(0, 2) + ':' + padded.substring(2)
}
function etMinutes() {
  try {
    const s = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false })
    const [h, m] = s.split(':')
    return parseInt(h) * 60 + parseInt(m)
  } catch { return new Date().getHours() * 60 + new Date().getMinutes() }
}

/* ─── sub-components ─── */ // v4.37.1
function PostIconImg({ name, size = 44 }: { name: string; size?: number }) {
  const src = getPostIcon(name)
  if (src) return <img src={src} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
  return <span style={{ fontSize: size * 0.65, width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>📍</span>
}

function BadgeImg({ gold, size = 40 }: { gold: boolean; size?: number }) {
  return <img src={gold ? SUPERVISOR_BADGE : OFFICER_BADGE} alt="" style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }} />
}

function EquipIconBgImg({ type, size = 44 }: { type: 'radio' | 'key' | 'recorder'; size?: number }) {
  const src = type === 'key' ? KEY_ICON : type === 'recorder' ? REC_ICON : WALKIE_ICON_BG
  return <img src={src} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4 }} />
}

function TimeBadge({ from, to }: { from: string; to: string }) {
  const nowMin = etMinutes()
  let f = String(from).replace(':', '')
  if (f.length < 4) f = '0' + f
  let t = String(to).replace(':', '')
  if (t.length < 4) t = '0' + t
  const fM = parseInt(f.substring(0, 2)) * 60 + parseInt(f.substring(2))
  const tM = parseInt(t.substring(0, 2)) * 60 + parseInt(t.substring(2))
  let active = false, past = false
  if (fM <= tM) {
    if (nowMin > 1200 && tM < 720) { active = false; past = false }
    else { active = nowMin >= fM && nowMin <= tM; past = nowMin > tM }
  } else { active = nowMin >= fM || nowMin <= tM; past = nowMin > tM && nowMin < fM }
  if (active) return <div style={{ padding: '4px 10px', borderRadius: 20, background: '#166534', fontSize: 11, fontWeight: 700, color: '#4ade80', whiteSpace: 'nowrap', boxShadow: '0 0 8px rgba(74,222,128,0.5)' }}>● Now</div>
  if (past) return <div style={{ padding: '4px 10px', borderRadius: 20, background: 'var(--bg-elevated)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>✓ Done</div>
  return <div style={{ padding: '4px 10px', borderRadius: 20, background: 'var(--border)', fontSize: 11, fontWeight: 600, color: '#60a5fa', whiteSpace: 'nowrap', boxShadow: '0 0 8px rgba(96,165,250,0.4)' }}>Upcoming</div>
}

function SectionBox({ title, color = '#3b82f6', count, children }: { title: string; color?: string; count: number; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--bg-deep)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 12 }}>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ background: `${color}22`, color, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{count}</span>
      </div>
      <div>{children}</div>
    </div>
  )
}

function EqCard({ type, label, val, on }: { type: 'radio' | 'key' | 'recorder' | 'flashlight'; label: string; val: string; on: boolean }) {
  return (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {type === 'flashlight' ? (
        <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 24, height: 40 }}>
            <FlashSVG />
          </div>
        </div>
      ) : (
        <EquipIconBgImg type={type} size={44} />
      )}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 2, marginTop: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: on ? '#4ade80' : 'var(--text-hint)' }}>{val}</div>
    </div>
  )
}

/* ─── Main Component ─── */
interface Props { onBack: () => void }

export default function OfficerTaskView({ onBack }: Props) {
  const session = useAuthStore(s => s.session)
  const signOut = useAuthStore(s => s.signOut)
  const { personnel, secondaryTasks, shiftInfo, notes } = useMobileStore()
  const syncStatus = useSyncStore(s => s.status)
  const isExec = isExecRole(session?.rank ?? '')

  // Officers are locked to their own call sign — no select screen
  const [selectedCS, setSelectedCS] = useState(() => {
    if (!isExec) return session?.callSign ?? ''
    try { return sessionStorage.getItem('fom_otv_cs') ?? (session?.callSign ?? '') }
    catch { return session?.callSign ?? '' }
  })
  const [mode, setMode] = useState<'select' | 'dashboard'>(() => {
    if (!isExec) return 'dashboard'
    return 'select'
  })
  const [inputCS, setInputCS] = useState('')
  const [showProfile, setShowProfile] = useState(false)

  // Force a fresh pull every time this view mounts.
  // Critical for Capacitor: the 30s interval may be suspended when the app is
  // backgrounded, so we can't rely on it having run recently. A mount-time pull
  // ensures the officer sees current equipment, posts, and tasks immediately
  // without needing to sign out and back in.
  useEffect(() => {
    import('../../stores/sync').then(({ useSyncStore }) => {
      useSyncStore.getState().forcePull()
    }).catch(() => {})
  }, [])

  useEffect(() => { try { sessionStorage.setItem('fom_otv_cs', selectedCS) } catch { /* silent */ } }, [selectedCS])
  useEffect(() => { try { sessionStorage.setItem('fom_otv_mode', mode) } catch { /* silent */ } }, [mode])

  const dotColor = syncColor(syncStatus)
  const searchCS = selectedCS || (session?.callSign ?? '')
  const myData = personnel.find(p => normalizeCS(p.callSign) === normalizeCS(searchCS)) ?? personnel.find(p => p.name === searchCS) ?? null

  function selectOfficer(cs: string) { setSelectedCS(cs); setMode('dashboard') }

  /* ─── SELECT SCREEN (exec) ─── */
  if (mode === 'select') {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', background: 'var(--bg-deep)', color: 'var(--text-primary)', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', justifyContent: 'space-between', marginBottom: 16, paddingTop: 'env(safe-area-inset-top)' }}>
          <button onClick={() => { try { sessionStorage.removeItem('fom_otv_cs'); sessionStorage.removeItem('fom_otv_mode') } catch { /* silent */ } onBack() }} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 10, padding: '8px 16px', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, animation: 'pulse 2s infinite' }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{syncStatus === 'ok' ? 'Live' : syncStatus === 'pulling' ? '...' : 'Offline'}</span>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 6 }}>📋</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Officer Task App</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Select an officer to view their shift</div>
        </div>
        <div style={{ width: '100%', maxWidth: 400 }}>
          {personnel.filter(p => p.name).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 14, background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
              No personnel on duty yet.<br /><span style={{ fontSize: 12 }}>Waiting for desktop sync…</span>
            </div>
          ) : personnel.filter(p => p.name).map((p, i) => {
            const gold = isGold(p.role)
            const num = csNumber(p.callSign)
            return (
              <button key={i} onClick={() => selectOfficer(p.callSign || p.name)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 12, background: 'var(--bg-surface)', border: '1px solid var(--border)', color: '#e8edf4', cursor: 'pointer', textAlign: 'left', width: '100%', marginBottom: 8 }}>
                <div style={{ position: 'relative', flexShrink: 0, width: p.role === 'FCPD' ? 65 : 52, height: p.role === 'FCPD' ? 89 : 72 }}>
                  <img src={getBadgeImg(p.role)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  {p.role !== 'FCPD' && (
                    <div style={{ position: 'absolute', top: '48%', left: '48%', transform: 'translate(-50%,-50%)', fontWeight: 900, fontSize: 13, color: '#000', fontFamily: 'Arial Black,sans-serif', textAlign: 'center', lineHeight: 1, pointerEvents: 'none' }}>{num}</div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName(p.name, p.role, p.rank)}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{p.callSign} · {roleLabel(p.role)}</div>
                </div>
                <div style={{ fontSize: 16, color: 'var(--text-hint)' }}>›</div>
              </button>
            )
          })}
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 16, marginBottom: 8, fontWeight: 600 }}>Or enter call sign:</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" value={inputCS} placeholder="S-21" onChange={e => setInputCS(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { const cs = inputCS.trim().toUpperCase(); if (cs) selectOfficer(cs) } }}
              style={{ flex: 1, padding: '12px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg-surface)', color: '#fff', fontSize: 16, fontWeight: 700, textAlign: 'center', letterSpacing: 2, outline: 'none' }} />
            <button onClick={() => { const cs = inputCS.trim().toUpperCase(); if (cs) selectOfficer(cs) }} disabled={!inputCS.trim()}
              style={{ padding: '12px 20px', borderRadius: 12, border: 'none', background: inputCS.trim() ? '#2563eb' : 'var(--text-hint)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: inputCS.trim() ? 'pointer' : 'not-allowed' }}>Go</button>
          </div>
        </div>
      </div>
    )
  }

  /* ─── NOT FOUND / SHIFT ENDED ─── */
  if (!myData) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', background: 'var(--bg-deep)', color: 'var(--text-primary)', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🌙</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>No Active Shift</div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 32, maxWidth: 280 }}>
          {isExec
            ? `"${searchCS}" is not on duty this shift.`
            : `Your shift has ended or not yet started. You will be notified when your next shift begins.`
          }
        </div>
        {isExec ? (
          <button
            onClick={() => setMode('select')}
            style={{ padding: '12px 32px', borderRadius: 12, border: 'none', background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}
          >← Back to List</button>
        ) : (
          <button
            onClick={() => { try { sessionStorage.removeItem('fom_otv_cs'); sessionStorage.removeItem('fom_otv_mode') } catch { /* silent */ } signOut() }}
            style={{ padding: '12px 32px', borderRadius: 12, border: '1px solid rgba(239,68,68,0.3)', background: 'none', color: '#ef4444', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
          >🚪 Sign Out</button>
        )}
      </div>
    )
  }

  /* ─── DASHBOARD ─── */
  const posts = (myData.posts ?? []).filter(p => typeof p === 'object' && !p.isBreak && p.post !== 'Break')
  const breaks = (myData.posts ?? []).filter(p => typeof p === 'object' && (p.isBreak || p.post === 'Break'))
  const tasks = getTasksForCS(secondaryTasks, myData.callSign)
  const gold = isGold(myData.role)

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-deep)', color: 'var(--text-primary)', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {/* Static hero header */}
      <div style={{ background: 'linear-gradient(135deg,#0a1830,#1e40af)', padding: '16px 16px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingTop: 'env(safe-area-inset-top)' }}>
          <button
            onClick={() => {
              if (isExec) {
                setMode('select')
                setSelectedCS('')
                try { sessionStorage.removeItem('fom_otv_cs') } catch { /* silent */ }
              } else {
                onBack()
              }
            }}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >← Back</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{syncStatus === 'ok' ? 'Live' : 'Offline'}</span>
            </div>
            <div
              onClick={() => setShowProfile(true)}
              title={`${session?.callSign} · ${session?.rank}`}
              style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(201,168,76,0.25)', border: '1.5px solid rgba(201,168,76,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, cursor: 'pointer' }}
            >👤</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative', flexShrink: 0, width: myData.role === 'FCPD' ? 113 : 90, height: myData.role === 'FCPD' ? 154 : 124 }}>
            <img src={getBadgeImg(myData.role)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            {myData.role !== 'FCPD' && (
              <div style={{ position: 'absolute', top: '48%', left: '48%', transform: 'translate(-50%,-50%)', fontWeight: 900, fontSize: 20, color: '#000', fontFamily: 'Arial Black,sans-serif', textAlign: 'center', lineHeight: 1, pointerEvents: 'none' }}>{csNumber(myData.callSign)}</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{displayName(myData.name, myData.role, myData.rank)}</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', fontWeight: 600, marginTop: 2 }}>{myData.callSign} · {roleLabel(myData.role)}</div>
            {(myData.shiftFrom || myData.shiftTo) && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>🕐 {fmtTime(myData.shiftFrom)} — {fmtTime(myData.shiftTo)}</div>}
          </div>
        </div>
      </div>

      {/* Scrolling body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {shiftInfo.date && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>📅 {shiftInfo.date}</div>
            {shiftInfo.shift && <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>🌤️ {shiftInfo.shift}</div>}
          </div>
        )}
        <SectionBox title="My Equipment" color="#22c55e" count={[myData.radio, myData.key, myData.recorder].filter(Boolean).length}>
          <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <EqCard type="radio" label="Radio" val={myData.radio || '—'} on={!!myData.radio} />
            <EqCard type="key" label="Key" val={myData.key || '—'} on={!!myData.key} />
            <EqCard type="recorder" label="Recorder" val={myData.recorder || '—'} on={!!myData.recorder} />
          </div>
        </SectionBox>
        <SectionBox title="Post Assignments" color="#3b82f6" count={posts.length}>
          {posts.length === 0
            ? <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-hint)', fontSize: 13, fontStyle: 'italic' }}>No posts assigned yet</div>
            : posts.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
              <PostIconImg name={typeof p === 'object' ? p.post : ''} size={44} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>{typeof p === 'object' ? p.post : ''}</div>
                {typeof p === 'object' && (p.from || p.to) && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{fmtTime(p.from)} — {fmtTime(p.to)}</div>}
              </div>
              {typeof p === 'object' && p.from && p.to && <TimeBadge from={p.from} to={p.to} />}
            </div>
          ))}
        </SectionBox>
        {breaks.length > 0 && (
          <SectionBox title="Break Schedule" color="#f59e0b" count={breaks.length}>
            {breaks.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>☕</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#fbbf24' }}>Break</div>
                  {typeof b === 'object' && (b.from || b.to) && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{fmtTime(b.from)} — {fmtTime(b.to)}</div>}
                  {typeof b === 'object' && b.coveredBy && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Covered by: {b.coveredBy}</div>}
                </div>
                {typeof b === 'object' && b.from && b.to && <TimeBadge from={b.from} to={b.to} />}
              </div>
            ))}
          </SectionBox>
        )}
        {tasks.length > 0 && (
          <SectionBox title="Secondary Tasks" color="#a855f7" count={tasks.length}>
            {tasks.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                <PostIconImg name={t.task || t.label} size={44} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9' }}>{t.label || t.task}</div>
                  {t.detail && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{t.detail}</div>}
                  {t.time && <div style={{ fontSize: 12, color: '#a855f7', marginTop: 2 }}>🕐 {t.time}</div>}
                </div>
              </div>
            ))}
          </SectionBox>
        )}
        {notes.length > 0 && (
          <SectionBox title="Notes / Special Details" color="#f59e0b" count={notes.length}>
            {notes.map((n, i) => (
              <div key={i} style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{n}</div>
            ))}
          </SectionBox>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0', paddingBottom: 'calc(24px + env(safe-area-inset-bottom))' }}>
          <button onClick={() => { try { sessionStorage.removeItem('fom_otv_cs'); sessionStorage.removeItem('fom_otv_mode') } catch { /* silent */ } signOut() }}
            style={{ background: 'none', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 24px', color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%', maxWidth: 360, textAlign: 'center' }}>🚪 Sign Out</button>
        </div>
      </div>
      {/* Profile modal */}
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  )
}
